<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Question } from "~/features/python/ch2/exercise/types/exercise.types";

const props = defineProps<{
  question: Question;
}>();

const emit = defineEmits<{
  (event: "submitted", value: number): void;
}>();

const selected = ref<number | null>(null);
const submitted = ref(false);

const isCorrect = computed(() => selected.value === props.question.correct);

watch(
  () => props.question.id,
  () => {
    selected.value = null;
    submitted.value = false;
  },
);

function submitOption(index: number) {
  if (submitted.value) {
    return;
  }

  selected.value = index;
  submitted.value = true;
  emit("submitted", index);
}
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="(option, index) in question.options ?? []"
      :key="`${question.id}-${index}`"
    >
      <button
        type="button"
        class="w-full rounded-2xl border px-4 py-3 text-left text-sm transition"
        :class="!submitted
          ? 'border-slate-200 bg-white text-slate-700'
          : index === question.correct
            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
            : selected === index
              ? 'border-rose-300 bg-rose-50 text-rose-800'
              : 'border-slate-200 bg-slate-50 text-slate-500'"
        :disabled="submitted"
        @click="submitOption(index)"
      >
        {{ option }}
      </button>
    </div>

    <div v-if="submitted" class="rounded-2xl bg-slate-50 p-4 text-sm">
      <p class="font-semibold" :class="isCorrect ? 'text-emerald-700' : 'text-rose-700'">
        {{ isCorrect ? "Correct" : "Incorrect" }}
      </p>
      <p class="mt-1 text-slate-700">{{ question.explanation }}</p>
    </div>
  </div>
</template>
